import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Snackbar,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Save, Refresh, LocalShipping } from '@mui/icons-material';
import { getPoolSettings, updatePoolSettings } from '../../services/groupShippingService';

const Settings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getPoolSettings();
      if (response.settings) {
        // Initialize settings for both air and sea if they don't exist
        const airSettings = response.settings.find(s => s.shipping_method === 'air') || {
          shipping_method: 'air',
          min_weight_threshold: 1.0,
          min_volume_threshold: 0,
          max_participants: 10,
          max_waiting_days: 5,
          auto_create_pools: true,
          is_active: true,
        };
        const seaSettings = response.settings.find(s => s.shipping_method === 'sea') || {
          shipping_method: 'sea',
          min_weight_threshold: 0,
          min_volume_threshold: 0.1,
          max_participants: 10,
          max_waiting_days: 5,
          auto_create_pools: true,
          is_active: true,
        };
        setSettings([airSettings, seaSettings]);
      }
    } catch (error) {
      showSnackbar('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await updatePoolSettings(settings);
      if (response.success) {
        showSnackbar('Settings saved successfully');
        loadSettings(); // Reload to get updated data
      } else {
        showSnackbar(response.message || 'Failed to save settings', 'error');
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const updateSetting = (index, key, value) => {
    setSettings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const getShippingMethodLabel = (method) => {
    return method === 'air' ? 'Air Freight' : 'Sea Freight';
  };

  const getShippingMethodColor = (method) => {
    return method === 'air' ? 'primary' : 'secondary';
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalShipping sx={{ fontSize: 32, color: '#DC143C' }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
            Group Shipping Pool Settings
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSettings}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B71C1C' } }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {settings.map((setting, index) => (
            <Grid item xs={12} md={6} key={setting.shipping_method || index}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {getShippingMethodLabel(setting.shipping_method)}
                    </Typography>
                    <Chip
                      label={setting.is_active ? 'Active' : 'Inactive'}
                      color={setting.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  {/* Active Status */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setting.is_active || false}
                        onChange={(e) => updateSetting(index, 'is_active', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Group Shipping"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', mb: 2, width: '100%' }}
                  />

                  {/* Thresholds */}
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Minimum Thresholds
                  </Typography>
                  {setting.shipping_method === 'air' ? (
                    <TextField
                      fullWidth
                      label="Minimum Weight (KG)"
                      type="number"
                      value={setting.min_weight_threshold || ''}
                      onChange={(e) => updateSetting(index, 'min_weight_threshold', parseFloat(e.target.value) || 0)}
                      helperText="Packages below this weight can join group shipping"
                      sx={{ mb: 2 }}
                      inputProps={{ step: '0.1', min: '0' }}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      label="Minimum Volume (CBM)"
                      type="number"
                      value={setting.min_volume_threshold || ''}
                      onChange={(e) => updateSetting(index, 'min_volume_threshold', parseFloat(e.target.value) || 0)}
                      helperText="Packages below this volume can join group shipping"
                      sx={{ mb: 2 }}
                      inputProps={{ step: '0.01', min: '0' }}
                    />
                  )}

                  {/* Pool Limits */}
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Pool Limits
                  </Typography>
                  <TextField
                    fullWidth
                    label="Maximum Participants"
                    type="number"
                    value={setting.max_participants || ''}
                    onChange={(e) => updateSetting(index, 'max_participants', parseInt(e.target.value) || 0)}
                    helperText="Maximum number of participants per pool"
                    sx={{ mb: 2 }}
                    inputProps={{ min: '1' }}
                  />
                  <TextField
                    fullWidth
                    label="Maximum Waiting Days"
                    type="number"
                    value={setting.max_waiting_days || ''}
                    onChange={(e) => updateSetting(index, 'max_waiting_days', parseInt(e.target.value) || 0)}
                    helperText="Days to wait for pool to fill before auto-cancellation"
                    sx={{ mb: 2 }}
                    inputProps={{ min: '1' }}
                  />

                  {/* Auto Create */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setting.auto_create_pools || false}
                        onChange={(e) => updateSetting(index, 'auto_create_pools', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Auto-create Pools"
                    labelPlacement="start"
                    sx={{ ml: 0, justifyContent: 'space-between', mb: 2, width: '100%' }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                    Automatically create pools when packages below threshold are detected
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> When a pool exceeds the maximum waiting days without meeting the threshold,
          it will be automatically cancelled and all participant packages will be converted back to individual shipping.
          Make sure to schedule the Celery task <code>check_and_cancel_expired_pools</code> to run daily.
        </Typography>
      </Alert>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
